import { useCallback, useEffect, useRef, useState } from 'react';
import { analyzeV0 } from 'api/analysisApi';
import { ApiError } from 'api/restClient';
import type { AnalysisResult } from 'types/analysis';

type Exchange = 'binance' | 'mexc';
type Status = 'idle' | 'loading' | 'success' | 'error' | 'aborted';

export interface UseAnalyzeV0Result {
  status: Status;
  data: AnalysisResult | null;
  error: string | null;
  /** When the current result was produced (ms epoch). */
  updatedAt: number | null;
  /**
   * Upload the given screenshot(s) and run the v0 (image → OCR → Claude) pipeline.
   * Call with no args to re-run the last uploaded set (e.g. the panel's ↻ Re-run).
   */
  run: (files?: File[]) => void;
  /** Cancel the in-flight upload/analysis (no-op if nothing is running). */
  abort: () => void;
  /** Clear any in-flight request and reset to idle (e.g. on exchange change). */
  reset: () => void;
}

/**
 * On-demand runner for the BrainerPro + Claude (v0 / v0.1) engine. Mirrors
 * useAnalyzeBrainer's status/data/error contract so AnalysisPanel can reuse the
 * same render branches, but `run` takes uploaded files instead of asset/tf params.
 * `useDbHeatmap` selects v0.1 (inject the cached DB liquidation heatmap) vs plain v0.
 */
export function useAnalyzeV0(exchange: Exchange, useDbHeatmap = false, timeframe?: string): UseAnalyzeV0Result {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const lastFilesRef = useRef<File[]>([]);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    lastFilesRef.current = [];
    setStatus('idle');
    setData(null);
    setError(null);
    setUpdatedAt(null);
  }, []);

  const run = useCallback(
    (files?: File[]) => {
      const toSend = files && files.length > 0 ? files : lastFilesRef.current;
      if (!toSend.length) return; // nothing to upload — ignore
      lastFilesRef.current = toSend;

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setStatus('loading');
      setError(null);

      analyzeV0(toSend, { mode: 'single', exchange, debug: true, useDbHeatmap, timeframe }, ctrl.signal)
        .then((res) => {
          if (!mountedRef.current || ctrl.signal.aborted) return;
          setData(res);
          setUpdatedAt(Date.now());
          setStatus('success');
        })
        .catch((e: unknown) => {
          if (!mountedRef.current || ctrl.signal.aborted) return;
          const msg =
            e instanceof ApiError
              ? `${e.message} (${e.status})`
              : e instanceof Error
                ? e.message
                : 'Analysis failed';
          setError(msg);
          setStatus('error');
        });
    },
    [exchange, useDbHeatmap, timeframe],
  );

  // User-initiated cancel: keep last data, land on 'aborted' (not 'idle').
  const abort = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    abortRef.current = null;
    setStatus('aborted');
  }, []);

  return { status, data, error, updatedAt, run, abort, reset };
}
